import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  listRenders,
  uploadRender,
  deleteRender,
  type AdminRender,
} from '../../lib/adminApi';
import { cls } from './shared';

const TYPES = ['master', 'environmental', 'dimensional'] as const;

export function RendersTab() {
  const { id } = useParams<{ id: string }>();
  const layoutId = parseInt(id!, 10);
  const [renders, setRenders] = useState<AdminRender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<string>('environmental');
  const [uploadIndex, setUploadIndex] = useState(1);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function load() {
    listRenders(layoutId)
      .then((r) => {
        setRenders(r.renders);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, [layoutId]);

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadRender(
        layoutId,
        file,
        uploadType,
        uploadType === 'environmental' ? uploadIndex : undefined,
      );
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(renderId: number) {
    if (!confirm('Remove this render record? The blob in Azure Storage will NOT be deleted.')) return;
    try {
      await deleteRender(renderId);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  }

  if (loading) return <p className="text-vari-gray text-sm">Loading…</p>;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;

  return (
    <>
      <div className={cls.formPanel}>
        <p className="text-sm font-medium text-vari-navy mb-3">Upload New Render</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-vari-gray block mb-1">Type</label>
            <select
              className={cls.select}
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          {uploadType === 'environmental' && (
            <div>
              <label className="text-xs text-vari-gray block mb-1">Index (1–5)</label>
              <input
                type="number"
                min={1}
                max={5}
                className="border border-vari-soft-gray rounded-md px-3 py-1.5 text-sm w-20 focus:outline focus:outline-1 focus:outline-vari-navy"
                value={uploadIndex}
                onChange={(e) => setUploadIndex(Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              />
            </div>
          )}
          <div>
            <label className="text-xs text-vari-gray block mb-1">Image file</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="text-sm"
            />
          </div>
          <button
            onClick={() => void handleUpload()}
            disabled={uploading}
            className={cls.btnSmPrimary}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        <p className="text-xs text-vari-gray mt-3">
          Uploaded to Azure Blob Storage; a thumbnail is generated automatically. Removing a record here does not delete the blob.
        </p>
      </div>

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>Type</th>
              <th className={cls.th}>Index</th>
              <th className={cls.th}>Blob path</th>
              <th className={cls.th}>Thumb</th>
              <th className={cls.th}>Preview</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {renders.map((r, i) => (
              <tr key={r.id} className={cls.tr(i % 2 === 1)}>
                <td className={cls.tdMono}>{r.type}</td>
                <td className={`${cls.td} text-vari-gray`}>{r.idx ?? '—'}</td>
                <td className="px-4 py-3 text-sm font-mono text-vari-gray truncate max-w-xs">{r.blobPath}</td>
                <td className={cls.td}>
                  {r.thumbUrl ? (
                    <img src={r.thumbUrl} alt="" className="h-10 w-14 object-cover rounded border border-vari-soft-gray" />
                  ) : (
                    <span className="text-xs text-vari-gray/50">—</span>
                  )}
                </td>
                <td className={cls.td}>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-vari-blue-accessible hover:underline">
                      View
                    </a>
                  ) : (
                    <span className="text-xs text-vari-gray/50">No URL</span>
                  )}
                </td>
                <td className={cls.tdRight}>
                  <button
                    onClick={() => void handleDelete(r.id)}
                    className={cls.btnDanger}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {renders.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No renders yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
